
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, ZoomIn, ZoomOut } from "lucide-react";
import { useState } from "react";

interface ImageZoomModalProps {
  isOpen?: boolean;
  imageUrl: string;
  onClose: () => void;
}

export const ImageZoomModal = ({ isOpen = true, imageUrl, onClose }: ImageZoomModalProps) => {
  const [zoomLevel, setZoomLevel] = useState(1);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen && imageUrl) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }
  }, [onClose, isOpen, imageUrl]);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !imageUrl) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="relative max-w-4xl max-h-full">
        {/* Controls */}
        <div className="absolute top-4 right-4 flex gap-2 z-10">
          <Button
            onClick={handleZoomOut}
            size="sm"
            variant="secondary"
            className="bg-white/90 hover:bg-white"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button
            onClick={handleZoomIn}
            size="sm"
            variant="secondary"
            className="bg-white/90 hover:bg-white"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            onClick={onClose}
            size="sm"
            variant="secondary"
            className="bg-white/90 hover:bg-white"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Zoom level indicator */}
        <div className="absolute top-4 left-4 bg-black/50 text-white px-2 py-1 rounded text-sm">
          {Math.round(zoomLevel * 100)}%
        </div>

        {/* Image */}
        <div className="overflow-auto max-h-[90vh] max-w-[90vw]">
          <img
            src={imageUrl}
            alt="Zoomed product"
            className="transition-transform duration-200 cursor-grab active:cursor-grabbing"
            style={{ transform: `scale(${zoomLevel})` }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
    </div>
  );
};
